/**
 * Saisies faites sans connexion, envoyées dès que le serveur répond.
 *
 * Chaque saisie porte un identifiant local « hl-… » qui sert aussi de clé
 * d'envoi (en-tête Idempotency-Key) : si l'envoi est répété, le serveur
 * renvoie la même réponse sans créer de doublon. Une saisie qui dépend
 * d'une autre (consultation d'un nouveau patient, encaissement de cette
 * consultation…) cite son identifiant local ; il est remplacé par le vrai
 * numéro une fois la première envoyée.
 */
import axios from 'axios'
import { API_BASE, messageErreur } from '@/api'
import store from '@/store'
import { file } from './stockage'
import { etat, signalerHorsLigne, signalerEnLigne, quandRetourConnexion } from './etat'

export const STATUTS = {
  EN_ATTENTE: 'EN_ATTENTE',
  ENVOYE: 'ENVOYE',
  ERREUR: 'ERREUR',   // refusée par le serveur : à corriger ou à abandonner
  BLOQUE: 'BLOQUE',   // dépend d'une saisie refusée
}

let ordre = Date.now()
let synchroEnCours = null

export function nouvelId() {
  const alea = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : (Date.now().toString(36) + Math.random().toString(36).slice(2))
  return 'hl-' + alea.replace(/[^a-zA-Z0-9-]/g, '')
}

function utilisateur() {
  const u = store.state.user || {}
  return { id: u.id || null, nom: [u.prenom, u.nom].filter(Boolean).join(' '), cliniqueId: u.clinique?.id || null }
}

/**
 * Ajoute une saisie à envoyer.
 * @param {object} s { id?, type, libelle, groupe, url (relative à /api/v1), corps, dependances: [id local…] }
 */
export async function ajouter(s) {
  const u = utilisateur()
  const item = {
    id: s.id || nouvelId(),
    ordre: ordre++,
    type: s.type,
    libelle: s.libelle,
    groupe: s.groupe || null,
    methode: s.methode || 'POST',
    url: s.url,
    corps: s.corps || {},
    dependances: s.dependances || [],
    creeLe: new Date().toISOString(),
    statut: STATUTS.EN_ATTENTE,
    utilisateurId: u.id,
    utilisateurNom: u.nom,
    cliniqueId: u.cliniqueId,
    resultat: null,
    erreur: null,
    tentatives: 0,
  }
  await file.enregistrer(item)
  await compter()
  return item
}

export async function liste() {
  return file.liste()
}

export async function compter() {
  const l = await file.liste()
  const miens = l.filter(i => i.utilisateurId === utilisateur().id)
  etat.enAttente = miens.filter(i => i.statut === STATUTS.EN_ATTENTE).length
  etat.enErreur = miens.filter(i => i.statut === STATUTS.ERREUR || i.statut === STATUTS.BLOQUE).length
  return l
}

/** Remplace les identifiants locaux par les vrais numéros (dans l'adresse et le contenu). */
function resoudre(valeur, correspondances) {
  if (typeof valeur === 'string') {
    if (correspondances[valeur] !== undefined) return correspondances[valeur]
    return valeur.replace(/hl-[a-zA-Z0-9-]+/g, m => (correspondances[m] !== undefined ? String(correspondances[m]) : m))
  }
  if (Array.isArray(valeur)) return valeur.map(v => resoudre(v, correspondances))
  if (valeur && typeof valeur === 'object') {
    const r = {}
    Object.keys(valeur).forEach(k => { r[k] = resoudre(valeur[k], correspondances) })
    return r
  }
  return valeur
}

/** Les champs « _… » servent seulement à l'affichage sur le poste. */
function sansInfosLocales(corps) {
  const r = {}
  Object.keys(corps || {}).forEach(k => { if (!k.startsWith('_')) r[k] = corps[k] })
  return r
}

/**
 * Envoie les saisies en attente de l'utilisateur connecté, dans l'ordre.
 * Un seul envoi à la fois sur le poste, même avec plusieurs onglets ouverts
 * (verrou partagé du navigateur) : l'onglet suivant relit la file à jour.
 */
export function synchroniser() {
  if (!synchroEnCours) {
    const verrou = typeof navigator !== 'undefined' && navigator.locks
      ? navigator.locks.request('clinique-file-hors-ligne', () => envoyerTout())
      : envoyerTout()
    synchroEnCours = verrou.finally(() => { synchroEnCours = null })
  }
  return synchroEnCours
}

async function envoyerTout() {
  const token = localStorage.getItem('token')
  const moi = utilisateur().id
  if (!token || !moi) return
  etat.envoiEnCours = true
  try {
    const tout = await file.liste()
    const correspondances = {}
    const parId = {}
    tout.forEach(i => {
      parId[i.id] = i
      if (i.statut === STATUTS.ENVOYE && i.resultat?.id !== undefined) correspondances[i.id] = i.resultat.id
    })

    for (const item of tout) {
      if (item.utilisateurId !== moi || item.statut === STATUTS.ENVOYE) continue

      const deps = item.dependances.map(d => parId[d]).filter(Boolean)
      if (deps.some(d => d.statut === STATUTS.ERREUR || d.statut === STATUTS.BLOQUE)) {
        if (item.statut !== STATUTS.BLOQUE) {
          item.statut = STATUTS.BLOQUE
          item.erreur = "En attente : une saisie dont elle dépend a été refusée."
          await file.enregistrer(item)
        }
        continue
      }
      if (deps.some(d => d.statut !== STATUTS.ENVOYE)) continue
      if (item.statut === STATUTS.ERREUR) continue // à relancer à la main

      item.tentatives++
      try {
        const r = await axios({
          method: item.methode,
          url: `${API_BASE}/api/v1/${resoudre(item.url, correspondances)}`,
          data: sansInfosLocales(resoudre(item.corps, correspondances)),
          timeout: 30000,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
            'Idempotency-Key': item.id,
            'X-Hors-Ligne-Le': item.creeLe,
          },
        })
        signalerEnLigne()
        item.statut = STATUTS.ENVOYE
        item.erreur = null
        item.envoyeLe = new Date().toISOString()
        item.resultat = r.data && typeof r.data === 'object'
          ? { id: r.data.id, numero: r.data.numero || r.data.numConsultation || r.data.numeroDossier || r.data.reference || null }
          : null
        if (item.resultat?.id !== undefined) correspondances[item.id] = item.resultat.id
        await file.enregistrer(item)
      } catch (e) {
        if (!e.response) {
          // Toujours pas de connexion : on réessaiera au retour du réseau
          signalerHorsLigne()
          await file.enregistrer(item)
          break
        }
        if (e.response.status === 401) {
          item.erreur = 'Session expirée : reconnectez-vous, les saisies seront envoyées ensuite.'
          await file.enregistrer(item)
          break
        }
        if (e.response.status === 409 && e.response.headers?.['retry-after']) {
          // Le même envoi est en cours de traitement ailleurs : on réessaiera
          await file.enregistrer(item)
          break
        }
        item.statut = e.response.status >= 500 ? STATUTS.EN_ATTENTE : STATUTS.ERREUR
        item.erreur = messageErreur(e, `Refusé par le serveur (code ${e.response.status}).`)
        await file.enregistrer(item)
        if (e.response.status >= 500) break
      }
    }

    // Les saisies envoyées sont gardées 24 h (historique du poste), puis effacées
    const limite = Date.now() - 24 * 3600 * 1000
    for (const i of await file.liste()) {
      if (i.statut === STATUTS.ENVOYE && new Date(i.envoyeLe || i.creeLe).getTime() < limite) await file.supprimer(i.id)
    }
    etat.derniereSynchro = new Date().toISOString()
  } finally {
    etat.envoiEnCours = false
    await compter()
  }
}

/** Relance une saisie refusée (après correction côté serveur, par exemple). */
export async function relancer(id) {
  const l = await file.liste()
  const item = l.find(i => i.id === id)
  if (!item) return
  item.statut = STATUTS.EN_ATTENTE
  item.erreur = null
  await file.enregistrer(item)
  // Les saisies qui en dépendaient reprennent aussi
  for (const d of l.filter(i => i.dependances.includes(id) && i.statut === STATUTS.BLOQUE)) {
    d.statut = STATUTS.EN_ATTENTE
    d.erreur = null
    await file.enregistrer(d)
  }
  await compter()
  return synchroniser()
}

/** Abandonne une saisie (et celles qui en dépendent). */
export async function abandonner(id) {
  const l = await file.liste()
  const aSupprimer = new Set([id])
  let ajout = true
  while (ajout) {
    ajout = false
    l.forEach(i => {
      if (!aSupprimer.has(i.id) && i.dependances.some(d => aSupprimer.has(d))) { aSupprimer.add(i.id); ajout = true }
    })
  }
  for (const x of aSupprimer) await file.supprimer(x)
  await compter()
}

// Envoi automatique au retour de la connexion, et toutes les 2 minutes
quandRetourConnexion(() => synchroniser())
if (typeof window !== 'undefined') {
  setInterval(() => { if (!etat.horsLigne && etat.enAttente > 0) synchroniser() }, 120000)
}
